import React, { useMemo } from "react";
import classnames from "classnames";
import NotificationAlert from "react-notification-alert";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Form,
  Input,
  InputGroupAddon,
  InputGroupText,
  InputGroup,
  Col,
  FormGroup,
  Label,
  Spinner,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import restClient from "../../../api/restClient";
import "../Login/Login.css";
import AuthHeroImage from "../components/AuthHeroImage";
import { AUTH_HERO_IMAGE_URLS, AUTH_HERO_SLOGANS } from "../constants";
import { formatPhoneNumber, validatePhoneNumber } from "../../../utils/phoneUtils";
import { getActiveColorStyle } from "../../../utils/colorUtils";
import { getSelectStyles } from "../../../utils/selectStyles";
import Select from "components/Select";

const IMAGE_URLS = AUTH_HERO_IMAGE_URLS;

const timeZoneOptions = [
  // US Timezones (Primary)
  { value: 'America/New_York', label: 'Eastern Time (ET) - New York, Miami, Atlanta' },
  { value: 'America/Chicago', label: 'Central Time (CT) - Chicago, Dallas, Houston' },
  { value: 'America/Denver', label: 'Mountain Time (MT) - Denver, Phoenix, Salt Lake City' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT) - Los Angeles, San Francisco, Seattle' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT) - Anchorage, Juneau' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST) - Honolulu, Maui' },

  // US Timezone Variations (Daylight Saving)
  { value: 'America/Detroit', label: 'Eastern Time - Detroit' },
  { value: 'America/Indiana/Indianapolis', label: 'Eastern Time - Indianapolis' },
  { value: 'America/Kentucky/Louisville', label: 'Eastern Time - Louisville' },
  { value: 'America/New_York', label: 'Eastern Time - New York (EDT/EST)' },
  { value: 'America/Chicago', label: 'Central Time - Chicago (CDT/CST)' },
  { value: 'America/Denver', label: 'Mountain Time - Denver (MDT/MST)' },
  { value: 'America/Phoenix', label: 'Mountain Time - Phoenix (MST - No DST)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time - Los Angeles (PDT/PST)' },

  // US Territories
  { value: 'America/Puerto_Rico', label: 'Atlantic Time - Puerto Rico' },
  { value: 'Pacific/Guam', label: 'Chamorro Time - Guam' },
  { value: 'Pacific/Samoa', label: 'Samoa Time - American Samoa' },

  // International Timezones
  { value: 'UTC-12', label: 'UTC-12 (Baker Island, Howland Island)' },
  { value: 'UTC-11', label: 'UTC-11 (American Samoa, Niue)' },
  { value: 'UTC-10', label: 'UTC-10 (Hawaii, Tahiti)' },
  { value: 'UTC-9', label: 'UTC-9 (Alaska, Gambier Islands)' },
  { value: 'UTC-8', label: 'UTC-8 (Pacific Time, Los Angeles)' },
  { value: 'UTC-7', label: 'UTC-7 (Mountain Time, Denver)' },
  { value: 'UTC-6', label: 'UTC-6 (Central Time, Chicago)' },
  { value: 'UTC-5', label: 'UTC-5 (Eastern Time, New York)' },
  { value: 'UTC-4', label: 'UTC-4 (Atlantic Time, Halifax)' },
  { value: 'UTC-3', label: 'UTC-3 (Brasília, Buenos Aires)' },
  { value: 'UTC-2', label: 'UTC-2 (South Georgia, Fernando de Noronha)' },
  { value: 'UTC-1', label: 'UTC-1 (Azores, Cape Verde)' },
  { value: 'UTC+0', label: 'UTC+0 (London, Lisbon)' },
  { value: 'UTC+1', label: 'UTC+1 (Paris, Berlin)' },
  { value: 'UTC+2', label: 'UTC+2 (Cairo, Helsinki)' },
  { value: 'UTC+3', label: 'UTC+3 (Moscow, Istanbul)' },
  { value: 'UTC+4', label: 'UTC+4 (Dubai, Baku)' },
  { value: 'UTC+5', label: 'UTC+5 (Tashkent, Karachi)' },
  { value: 'UTC+6', label: 'UTC+6 (Dhaka, Almaty)' },
  { value: 'UTC+7', label: 'UTC+7 (Bangkok, Jakarta)' },
  { value: 'UTC+8', label: 'UTC+8 (Beijing, Singapore)' },
  { value: 'UTC+9', label: 'UTC+9 (Tokyo, Seoul)' },
  { value: 'UTC+10', label: 'UTC+10 (Sydney, Melbourne)' },
  { value: 'UTC+11', label: 'UTC+11 (Solomon Islands, New Caledonia)' },
  { value: 'UTC+12', label: 'UTC+12 (Fiji, Kamchatka)' },
];

const Register = ({ activeColor = "primary" }) => {
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const authSelectStyles = useMemo(
    () => getSelectStyles(activeColorStyle, false),
    [activeColorStyle]
  );

  const handlePhoneChange = (e) => {
    const formattedValue = formatPhoneNumber(e.target.value);
    setState(s => ({ ...s, workPhone: formattedValue }));
    if (errors.workPhone) {
      setErrors(prev => ({ ...prev, workPhone: null }));
    }
  };

  const [state, setState] = React.useState({
    tenantName: "",
    name: "",
    email: "",
    workPhone: "",
    timeZone: "",
    password: "",
    nameFocus: false,
    emailFocus: false,
    passFocus: false,
    tenantFocus: false,
    phoneFocus: false,
    timeZoneFocus: false,
  });
  const [errors, setErrors] = React.useState({});
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const notificationAlertRef = React.useRef(null);
  const navigate = useNavigate();
  const [slideIndex, setSlideIndex] = React.useState(0);
  const [overlayActive, setOverlayActive] = React.useState(false); // overlay için state

  React.useEffect(() => {
    document.body.classList.toggle("register-page");
    return function cleanup() {
      document.body.classList.toggle("register-page");
    };
  }, []);


  React.useEffect(() => {
    let fadeInTimeout, switchTimeout, fadeOutTimeout;
    function startTransitionCycle() {
      // 1. Net halde bekle (ör. 5 sn)
      fadeInTimeout = setTimeout(() => {
        setOverlayActive(true); // 2. Overlay fade-in (siyah + blur)
        // 3. 1.5 sn sonra fotoğraf değişsin
        switchTimeout = setTimeout(() => {
          setSlideIndex((prev) => prev + 1);
          // 4. Yeni fotoğrafla overlay hala açık, 1.5 sn sonra overlay fade-out
          fadeOutTimeout = setTimeout(() => {
            setOverlayActive(false); // 5. Overlay fade-out (netleşir)
          }, 1500); // overlay yeni fotoğrafla 1.5 sn daha açık kalır
        }, 1500); // overlay açıkken eski fotoğraf 1.5 sn kalır
      }, 5000); // net halde 5 sn bekle
    }
    startTransitionCycle();
    const interval = setInterval(() => {
      startTransitionCycle();
    }, 8000); // toplam döngü: 5 sn net + 1.5 sn overlay + 1.5 sn yeni foto overlay = 8 sn
    return () => {
      clearInterval(interval);
      clearTimeout(fadeInTimeout);
      clearTimeout(switchTimeout);
      clearTimeout(fadeOutTimeout);
    };
  }, []);

  const showNotification = (message, type = "warning") => {
    notificationAlertRef.current.notificationAlert({
      place: "tr",
      message: <div>{message}</div>,
      type: type,
      icon: "tim-icons icon-alert-circle-exc",
      autoDismiss: 5,
    });
  };

  const validateForm = () => {
    const newErrors = {};

    // Tenant Name validation
    if (!state.tenantName.trim()) {
      newErrors.tenantName = "Tenant name is required";
    }

    // Full Name validation
    if (!state.name.trim()) {
      newErrors.name = "Full name is required";
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!state.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!emailRegex.test(state.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // Work Phone validation - US format
    const phoneError = validatePhoneNumber(state.workPhone);
    if (phoneError) {
      newErrors.workPhone = phoneError;
    }

    // Time Zone validation
    if (!state.timeZone) {
      newErrors.timeZone = "Time zone is required";
    }

    // Password validation
    if (!state.password) {
      newErrors.password = "Password is required";
    } else if (state.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleRegister = async (e) => {
    setLoading(true);
    e.preventDefault();

    if (!validateForm()) {
      showNotification('Please fix the errors in the form', "warning");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await restClient.post('register', {
        data: {
          full_name: state.name,
          email: state.email,
          password: state.password,
          work_phone: state.workPhone,
          time_zone: state.timeZone,
          tenant_name: state.tenantName,
        }
      });
      const token = response.data.jwt;
      localStorage.setItem("token", token);
      showNotification('Registration successful', "success");
      setTimeout(() => {
        setLoading(false);
        navigate('/login');
      }, 1000);
    } catch (error) {
      console.log("error123123", error);
      showNotification(error?.response?.data?.message || 'Registration failed', "warning");
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleRegister(event);
    }
  };

  return (
    <div className="wrapper d-flex" style={{ minHeight: "100vh", width: "100vw" }}>
      <div className="form-section d-flex align-items-center justify-content-center" style={{ flex: 1, background: "#1e1e2f", minWidth: 350, maxWidth: 450, width: "100%", padding: "0px", zIndex: 9999 }}>
        <Col className="ml-auto mr-auto" lg="10" md="12">
          <div className="rna-container">
            <NotificationAlert ref={notificationAlertRef} />
          </div>
          <Form style={{ paddingTop: 0 }} className="form" onSubmit={handleRegister} autoComplete="off">
            <Card className="card-login" style={{ background: 'transparent', boxShadow: 'none' }}>
              <div style={{ textAlign: 'center', color: '#fff', fontWeight: 600, fontSize: 28, marginBottom: 32, letterSpacing: 0.5 }}>Create your account</div>
              <CardBody>
                <FormGroup style={{ marginBottom: 8 }}>
                  <Label for="register-tenant" style={{ color: '#fff', fontWeight: 500, fontSize: 14, marginBottom: 4 }}>Tenant Name *</Label>
                  <InputGroup className={classnames({ "input-group-focus": state.tenantFocus })}>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="tim-icons icon-bank" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      id="register-tenant"
                      placeholder="Tenant Name"
                      type="text"
                      value={state.tenantName}
                      onChange={e => {
                        setState(s => ({ ...s, tenantName: e.target.value }));
                        if (errors.tenantName) {
                          setErrors(prev => ({ ...prev, tenantName: null }));
                        }
                      }}
                      onFocus={() => setState(s => ({ ...s, tenantFocus: true }))}
                      onBlur={() => setState(s => ({ ...s, tenantFocus: false }))}
                      autoComplete="off"
                      onKeyPress={handleKeyPress}
                      className={errors.tenantName ? "is-invalid" : ""}
                    />
                  </InputGroup>
                  {errors.tenantName && (
                    <div style={{ color: '#fd5d93', fontSize: '12px', marginTop: '4px' }}>
                      {errors.tenantName}
                    </div>
                  )}
                </FormGroup>
                <FormGroup style={{ marginBottom: 8 }}>
                  <Label for="register-name" style={{ color: '#fff', fontWeight: 500, fontSize: 14, marginBottom: 4 }}>Full Name *</Label>
                  <InputGroup className={classnames({ "input-group-focus": state.nameFocus })}>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="tim-icons icon-single-02" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      id="register-name"
                      placeholder="Full Name"
                      type="text"
                      value={state.name}
                      onChange={e => {
                        setState(s => ({ ...s, name: e.target.value }));
                        if (errors.name) {
                          setErrors(prev => ({ ...prev, name: null }));
                        }
                      }}
                      onFocus={() => setState(s => ({ ...s, nameFocus: true }))}
                      onBlur={() => setState(s => ({ ...s, nameFocus: false }))}
                      autoComplete="off"
                      onKeyPress={handleKeyPress}
                      className={errors.name ? "is-invalid" : ""}
                    />
                  </InputGroup>
                  {errors.name && (
                    <div style={{ color: '#fd5d93', fontSize: '12px', marginTop: '4px' }}>
                      {errors.name}
                    </div>
                  )}
                </FormGroup>
                <FormGroup style={{ marginBottom: 8 }}>
                  <Label for="register-email" style={{ color: '#fff', fontWeight: 500, fontSize: 14, marginBottom: 4 }}>Email address *</Label>
                  <InputGroup className={classnames({ "input-group-focus": state.emailFocus })}>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="tim-icons icon-email-85" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      id="register-email"
                      placeholder="Email"
                      type="email"
                      value={state.email}
                      onChange={e => {
                        setState(s => ({ ...s, email: e.target.value }));
                        if (errors.email) {
                          setErrors(prev => ({ ...prev, email: null }));
                        }
                      }}
                      onFocus={() => setState(s => ({ ...s, emailFocus: true }))}
                      onBlur={() => setState(s => ({ ...s, emailFocus: false }))}
                      autoComplete="off"
                      onKeyPress={handleKeyPress}
                      className={errors.email ? "is-invalid" : ""}
                    />
                  </InputGroup>
                  {errors.email && (
                    <div style={{ color: '#fd5d93', fontSize: '12px', marginTop: '4px' }}>
                      {errors.email}
                    </div>
                  )}
                </FormGroup>
                <FormGroup style={{ marginBottom: 8 }}>
                  <Label for="register-phone" style={{ color: '#fff', fontWeight: 500, fontSize: 14, marginBottom: 4 }}>Work Phone *</Label>
                  <InputGroup className={classnames({ "input-group-focus": state.phoneFocus })}>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="tim-icons icon-mobile" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      id="register-phone"
                      placeholder="Work Phone (e.g., +1-555-123-4567)"
                      type="tel"
                      value={state.workPhone}
                      onChange={handlePhoneChange}
                      onFocus={() => setState(s => ({ ...s, phoneFocus: true }))}
                      onBlur={() => setState(s => ({ ...s, phoneFocus: false }))}
                      autoComplete="off"
                      onKeyPress={handleKeyPress}
                      className={errors.workPhone ? "is-invalid" : ""}
                    />
                  </InputGroup>
                  {errors.workPhone && (
                    <div style={{ color: '#fd5d93', fontSize: '12px', marginTop: '4px' }}>
                      {errors.workPhone}
                    </div>
                  )}
                </FormGroup>
                <FormGroup style={{ marginBottom: 8 }}>
                  <Label for="register-timezone" style={{ color: '#fff', fontWeight: 500, fontSize: 14, marginBottom: 4 }}>Time Zone *</Label>
                  <div
                    className="auth-select-wrapper"
                    style={{
                    border: errors.timeZone ? '1px solid #fd5d93' : '1px solid #2b3553',
                    borderRadius: '0px',
                    transition: 'border-color 0.15s ease-in-out',
                    backgroundColor: '#1E1E2F'
                  }}>
                    <Select
                      activeColor={activeColor}
                      className="react-select"
                      placeholder="Select your time zone"
                      name="timeZone"
                      isSearchable
                      closeMenuOnSelect={true}
                      styles={authSelectStyles}
                      value={timeZoneOptions.find(opt => opt.value === state.timeZone) || null}
                      onChange={(option) => {
                        setState(s => ({ ...s, timeZone: option?.value || "" }));
                        if (errors.timeZone) {
                          setErrors(prev => ({ ...prev, timeZone: null }));
                        }
                      }}
                      options={timeZoneOptions}
                    />
                  </div>
                  {errors.timeZone && (
                    <div style={{ color: '#fd5d93', fontSize: '12px', marginTop: '4px' }}>
                      {errors.timeZone}
                    </div>
                  )}
                </FormGroup>
                <FormGroup style={{ marginBottom: 8 }}>
                  <Label for="register-password" style={{ color: '#fff', fontWeight: 500, fontSize: 14, marginBottom: 4 }}>Password * (min 6 characters)</Label>
                  <InputGroup className={classnames({ "input-group-focus": state.passFocus })}>
                    <InputGroupAddon addonType="prepend">
                      <InputGroupText>
                        <i className="tim-icons icon-lock-circle" />
                      </InputGroupText>
                    </InputGroupAddon>
                    <Input
                      id="register-password"
                      placeholder="Password (min 6 characters)"
                      type={showPassword ? "text" : "password"}
                      value={state.password}
                      onChange={e => {
                        setState(s => ({ ...s, password: e.target.value }));
                        if (errors.password) {
                          setErrors(prev => ({ ...prev, password: null }));
                        }
                      }}
                      onFocus={() => setState(s => ({ ...s, passFocus: true }))}
                      onBlur={() => setState(s => ({ ...s, passFocus: false }))}
                      autoComplete="new-password"
                      onKeyPress={handleKeyPress}
                      className={errors.password ? "is-invalid" : ""}
                    />
                    <InputGroupAddon addonType="append">
                      <InputGroupText
                        tag="button"
                        type="button"
                        onClick={() => setShowPassword((prev) => !prev)}
                        style={{
                          cursor: "pointer",
                          background: "transparent",
                          border: "none",
                          color: "#fff",
                        }}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        <i className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"} />
                      </InputGroupText>
                    </InputGroupAddon>
                  </InputGroup>
                  {errors.password && (
                    <div style={{ color: '#fd5d93', fontSize: '12px', marginTop: '4px' }}>
                      {errors.password}
                    </div>
                  )}
                </FormGroup>
              </CardBody>
              <CardFooter>
                <Button
                  block
                  className="mb-3"
                  type="submit"
                  size="lg"
                  disabled={loading}
                  style={{
                    ...activeColorStyle,
                    border: 'none',
                    color: 'white',
                    boxShadow: 'none',
                    opacity: 1,
                    fontWeight: '600',
                    fontSize: '16px',
                    borderRadius: '0'
                  }}
                >
                  {loading ? <Spinner /> : 'Register'}
                </Button>
                <Button
                  block
                  className="auth-secondary-btn"
                  type="button"
                  size="md"
                  onClick={() => navigate('/login')}
                  style={{
                    marginTop: 8,
                    backgroundColor: 'transparent',
                    border: 'none',
                    boxShadow: 'none',
                    margin: "0px",
                    opacity: 1,
                    fontWeight: '600',
                    fontSize: '14px',
                    borderRadius: '0',
                    color: '#fff',
                  }}
                >
                  Back to Login
                </Button>
              </CardFooter>
            </Card>
          </Form>
        </Col>
      </div>
      <AuthHeroImage
        imageUrls={IMAGE_URLS}
        slogans={AUTH_HERO_SLOGANS}
        slideIndex={slideIndex}
        overlayActive={overlayActive}
      />
    </div>
  );
};

export default Register;
